import { Alert, Anchor, Space, Table, Tag, Typography } from 'antd'
import { ArrowRightOutlined } from '@ant-design/icons'
import type { ManualContent, ManualTable } from './manualContent'

const { Paragraph, Text, Title } = Typography

function tableRows(table: ManualTable) {
  return table.rows.map((row, rowIndex) => {
    const item: Record<string, string | number> = { key: rowIndex }
    row.forEach((value, columnIndex) => {
      item[`col${columnIndex}`] = value
    })
    return item
  })
}

function tableColumns(table: ManualTable) {
  return table.columns.map((title, columnIndex) => ({
    title,
    dataIndex: `col${columnIndex}`,
    key: `col${columnIndex}`,
    render: (value: string) => <Text>{value || '-'}</Text>,
  }))
}

interface ManualPageProps {
  content: ManualContent
}

export default function ManualPage({ content }: ManualPageProps) {
  return (
    <div className="manual-page">
      <div className="manual-header">
        <Space size={[8, 8]} wrap>
          <Tag color="blue">{content.audience}</Tag>
          <Tag>更新: {content.updatedAt}</Tag>
        </Space>
        <Title level={3}>{content.title}</Title>
        <Paragraph type="secondary">{content.subtitle}</Paragraph>
        <div className="manual-highlights">
          {content.highlights.map((item) => (
            <div className="manual-highlight" key={item.label}>
              <Text type="secondary">{item.label}</Text>
              <Text strong>{item.value}</Text>
            </div>
          ))}
        </div>
      </div>

      <div className="manual-layout">
        <main className="manual-main">
          {content.sections.map((section) => (
            <section className="manual-section" id={section.id} key={section.id}>
              <Title level={4}>{section.title}</Title>
              {section.summary && <Paragraph>{section.summary}</Paragraph>}

              {section.flow && (
                <div className="manual-flow" aria-label={`${section.title}流程图`}>
                  {section.flow.map((item, index) => (
                    <div className="manual-flow-segment" key={item.step}>
                      <div className="manual-flow-card">
                        <div className="manual-flow-step">{item.step}</div>
                        <Text strong>{item.title}</Text>
                        <Tag color="blue">{item.page}</Tag>
                        <Text>{item.action}</Text>
                        <Text type="secondary">{item.result}</Text>
                      </div>
                      {index < section.flow!.length - 1 && (
                        <ArrowRightOutlined className="manual-flow-arrow" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {section.example && (
                <div className="manual-example">
                  <div className="manual-example-summary">
                    <Text strong>{section.example.title}</Text>
                    <Text>{section.example.scenario}</Text>
                    <div className="manual-example-quote">
                      <Text type="secondary">{section.example.input}</Text>
                    </div>
                    <Text type="secondary">{section.example.target}</Text>
                  </div>
                  <div className="manual-example-steps">
                    {section.example.steps.map((item, index) => (
                      <div className="manual-example-step" key={`${item.title}-${index}`}>
                        <div className="manual-example-step-index">{index + 1}</div>
                        <div className="manual-example-step-body">
                          <Space size={[6, 6]} wrap>
                            <Text strong>{item.title}</Text>
                            <Tag>{item.page}</Tag>
                          </Space>
                          <Text>{item.action}</Text>
                          <Text type="secondary">预期结果：{item.expected}</Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {section.steps && (
                <ol className="manual-list">
                  {section.steps.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ol>
              )}

              {section.checklist && (
                <div className="manual-checklist">
                  <Text strong>检查清单</Text>
                  <ul className="manual-list">
                    {section.checklist.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {section.table && (
                <Table
                  className="manual-table"
                  size="small"
                  pagination={false}
                  dataSource={tableRows(section.table)}
                  columns={tableColumns(section.table)}
                  scroll={{ x: 'max-content' }}
                />
              )}

              {section.warning && (
                <Alert className="manual-alert" type="warning" showIcon message={section.warning} />
              )}

              {section.tips && (
                <div className="manual-tips">
                  <Text strong>提示</Text>
                  <ul className="manual-list">
                    {section.tips.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          ))}
        </main>

        <aside className="manual-aside">
          <Text strong>快速索引</Text>
          <Anchor
            affix={false}
            items={content.sections.map((section) => ({
              key: section.id,
              href: `#${section.id}`,
              title: section.title,
            }))}
          />
          <div className="manual-status-panel">
            <Text strong>关键状态</Text>
            <div className="manual-status-list">
              {content.statusLegend.map((item) => (
                <div className="manual-status-item" key={item.label}>
                  <Tag color={item.tone || 'default'}>{item.label}</Tag>
                  <Text type="secondary">{item.value}</Text>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
